import React from 'react';
import { Table, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ReservationReportResponseV4 } from '../../../../services/cm-group-report/response/ReservationReportResponseV4';
import { format } from 'date-fns';

interface ReportReservationTableV4Props {
    data: ReservationReportResponseV4;
}

const ReportReservationTableV4: React.FC<ReportReservationTableV4Props> = ({ data }) => {
    // Dapatkan senarai bulan dari 'total_month' untuk dijadikan kepala lajur
    const monthHeaders = data.total_month.map(m => m.month);
    const formattedMonthHeaders = monthHeaders.map(m => format(new Date(m), 'MMMM yyyy'));

    // Dapatkan senarai sumber/ejen dari 'total_source' untuk dijadikan baris
    const sourceRows = data.total_source;

    // Peta data untuk akses pantas: Map<sourceName, Map<month, nb_reservations>>
    const dataMap = new Map<string, Map<string, number>>();
    data.data.forEach(item => {
        if (!dataMap.has(item.name)) {
            dataMap.set(item.name, new Map());
        }
        dataMap.get(item.name)?.set(item.month, item.nb_reservations);
    });

    const exportToPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.text("Reservation Report", 14, 10);

        const head = [["Source", ...formattedMonthHeaders, "Total"]];

        const body = sourceRows.map(source => [
            source.name,
            ...monthHeaders.map(month => {
                const value = dataMap.get(source.name)?.get(month) || 0;
                return value > 0 ? value : '-';
            }),
            source.nb_reservations
        ]);

        const foot = [[
            "Total",
            ...data.total_month.map(monthTotal => monthTotal.nb_reservations),
            data.total
        ]];

        autoTable(doc, { head, body, foot });
        doc.save("ReportReservationV4.pdf");
    };

    const exportToExcel = () => {
        const header = ["Source", ...formattedMonthHeaders, "Total"];

        const body = sourceRows.map(source => [
            source.name,
            ...monthHeaders.map(month => {
                const value = dataMap.get(source.name)?.get(month) || 0;
                return value > 0 ? value : 0; // Guna 0 untuk Excel agar boleh dijumlahkan
            }),
            source.nb_reservations
        ]);

        const footer = [
            "Total",
            ...data.total_month.map(monthTotal => monthTotal.nb_reservations),
            data.total
        ];

        const wsData = [header, ...body, footer];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Reservation Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "ReportReservationV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Source</th>
                            {monthHeaders.map((month) => (
                                <th key={month} className="text-center">{format(new Date(month), 'MMMM yyyy')}</th>
                            ))}
                            <th className="text-center">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sourceRows.map(source => (
                            <tr key={source.name}>
                                <td>{source.name}</td>
                                {monthHeaders.map(month => {
                                    const value = dataMap.get(source.name)?.get(month) || 0;
                                    return <td key={month} className="text-center">{value > 0 ? value : '-'}</td>;
                                })}
                                <td className="text-center"><strong>{source.nb_reservations > 0 ? source.nb_reservations : '-'}</strong></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            {data.total_month.map(monthTotal => (
                                <td key={monthTotal.month} className="text-center"><strong>{monthTotal.nb_reservations > 0 ? monthTotal.nb_reservations : '-'}</strong></td>
                            ))}
                            <td className="text-center"><strong>{data.total > 0 ? data.total : '-'}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default ReportReservationTableV4;